import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, CalendarPlus, Video, Download, Users } from "lucide-react";
import { LxpLayout } from "@/components/lexpractiq/LxpLayout";
import {
  listUpcomingSessions, createPublicSession, rsvpToSession, sessionICS,
} from "@/lib/api/lxp-sessions.functions";
import { getMyMentorProfile } from "@/lib/api/lxp-mentors.functions";

export const Route = createFileRoute("/_authenticated/lexpractiq/mentors/q-and-a")({
  head: () => ({ meta: [
    { title: "Mentor Q&A sessions | SA LexPractiq" },
    { name: "description", content: "Browse and RSVP for live Q&A sessions with SA LexPractiq mentors." },
  ] }),
  component: SessionsPage,
});

function SessionsPage() {
  const listFn = useServerFn(listUpcomingSessions);
  const createFn = useServerFn(createPublicSession);
  const rsvpFn = useServerFn(rsvpToSession);
  const icsFn = useServerFn(sessionICS);
  const meFn = useServerFn(getMyMentorProfile);
  const qc = useQueryClient();

  const me = useQuery({ queryKey: ["my-mentor-profile"], queryFn: () => meFn() });
  const isMentor = !!me.data?.profile?.published;

  const q = useQuery({ queryKey: ["upcoming-sessions"], queryFn: () => listFn() });

  const rsvpM = useMutation({
    mutationFn: (v: { sessionId: string; going: boolean }) => rsvpFn({ data: v }),
    onSuccess: (r) => { toast.success(r.going ? "You're in!" : "RSVP cancelled"); qc.invalidateQueries({ queryKey: ["upcoming-sessions"] }); },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  async function downloadIcs(sessionId: string) {
    try {
      const r = await icsFn({ data: { sessionId } });
      const blob = new Blob([r.ics], { type: "text/calendar" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = r.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) { toast.error(e?.message ?? "Could not build invite"); }
  }

  return (
    <LxpLayout>
      <div className="lxp-container" style={{ padding: "2rem 1.25rem" }}>
        <header>
          <p className="text-xs uppercase tracking-[0.3em] text-emerald">Mentor Network</p>
          <h1 className="mt-1 font-display text-3xl text-navy">Live Q&A sessions</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Practising attorneys host regular open sessions. RSVP for the ones you can attend — we'll send a calendar invite.
          </p>
        </header>

        {isMentor && <MentorCreateSession onCreate={async (payload) => {
          try { await createFn({ data: payload }); toast.success("Session scheduled"); qc.invalidateQueries({ queryKey: ["upcoming-sessions"] }); }
          catch (e: any) { toast.error(e?.message ?? "Failed"); }
        }} />}

        <section className="mt-8 grid gap-4 md:grid-cols-2">
          {(q.data?.sessions ?? []).map((s: any) => (
            <article key={s.id} className="rounded-xl border border-input bg-card p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-display text-lg text-navy">{s.title}</h2>
                  <p className="text-xs text-muted-foreground">with {s.mentor?.display_name ?? "Mentor"}</p>
                </div>
                <span className="rounded-full bg-emerald/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-emerald">
                  <Users className="mr-1 inline h-3 w-3" />{s.rsvp_count}/{s.capacity}
                </span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">{s.description}</p>
              <p className="mt-3 text-sm">
                <CalendarCheck className="mr-1 inline h-4 w-4 text-gold" />
                {new Date(s.starts_at).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" })} · {s.duration_min}min
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  onClick={() => rsvpM.mutate({ sessionId: s.id, going: !s.i_rsvpd })}
                  disabled={rsvpM.isPending || (!s.i_rsvpd && s.rsvp_count >= s.capacity)}
                  className={`inline-flex items-center gap-1 rounded-md px-3 py-2 text-xs font-semibold ${
                    s.i_rsvpd ? "bg-emerald text-emerald-foreground" : "bg-navy text-navy-foreground hover:bg-navy/90"
                  } disabled:opacity-50`}
                >
                  <CalendarPlus className="h-3.5 w-3.5" /> {s.i_rsvpd ? "You're going" : "RSVP"}
                </button>
                <button onClick={() => downloadIcs(s.id)} className="inline-flex items-center gap-1 rounded-md border border-input px-3 py-2 text-xs font-semibold text-navy hover:bg-accent">
                  <Download className="h-3.5 w-3.5" /> Add to calendar
                </button>
                {s.meeting_url && (
                  <a href={s.meeting_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-input px-3 py-2 text-xs font-semibold text-navy hover:bg-accent">
                    <Video className="h-3.5 w-3.5" /> Join link
                  </a>
                )}
              </div>
            </article>
          ))}
          {(q.data?.sessions ?? []).length === 0 && (
            <p className="text-sm text-muted-foreground">No upcoming sessions. Check back soon.</p>
          )}
        </section>
      </div>
    </LxpLayout>
  );
}

function MentorCreateSession({ onCreate }: { onCreate: (p: { title: string; description?: string; startsAt: string; durationMin: number; capacity: number; meetingUrl?: string }) => void }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", description: "", startsAt: "", durationMin: 45, capacity: 25, meetingUrl: "" });
  return (
    <div className="mt-6 rounded-xl border-2 border-dashed border-gold bg-accent/50 p-5">
      <button onClick={() => setOpen(!open)} className="text-sm font-semibold text-navy">
        {open ? "− Cancel" : "+ Schedule a Q&A session"}
      </button>
      {open && (
        <div className="mt-4 grid gap-3">
          <input placeholder="Session title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="rounded-md border border-input bg-background px-3 py-2 text-sm" />
          <textarea placeholder="Description" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="rounded-md border border-input bg-background px-3 py-2 text-sm" />
          <div className="grid gap-3 sm:grid-cols-3">
            <input type="datetime-local" value={f.startsAt} onChange={(e) => setF({ ...f, startsAt: e.target.value })} className="rounded-md border border-input bg-background px-3 py-2 text-sm" />
            <input type="number" min={15} max={240} value={f.durationMin} onChange={(e) => setF({ ...f, durationMin: Number(e.target.value) })} className="rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="Minutes" />
            <input type="number" min={1} max={500} value={f.capacity} onChange={(e) => setF({ ...f, capacity: Number(e.target.value) })} className="rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="Capacity" />
          </div>
          <input placeholder="Meeting URL (Zoom / Teams / Meet)" value={f.meetingUrl} onChange={(e) => setF({ ...f, meetingUrl: e.target.value })} className="rounded-md border border-input bg-background px-3 py-2 text-sm" />
          <button
            onClick={() => {
              if (!f.title || !f.startsAt) return toast.error("Title and start time are required");
              onCreate({
                title: f.title, description: f.description || undefined,
                startsAt: new Date(f.startsAt).toISOString(),
                durationMin: f.durationMin, capacity: f.capacity,
                meetingUrl: f.meetingUrl || undefined,
              });
              setOpen(false);
              setF({ title: "", description: "", startsAt: "", durationMin: 45, capacity: 25, meetingUrl: "" });
            }}
            className="justify-self-start rounded-md bg-navy px-4 py-2 text-sm font-semibold text-navy-foreground"
          >Publish session</button>
        </div>
      )}
    </div>
  );
}
